import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, Badge, Form, Button, Accordion } from 'react-bootstrap';
import { FiMessageSquare, FiClock, FiUser, FiSend, FiEdit3 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Header, BottomNav, SkeletonList, ImageViewerModal } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../context/DateContext';
import { cleaningApi, memoApi, messageApi, CleaningItem, MemoComment, Memo } from '../api/client';
import { formatRoomAddress, formatDbTime } from '../utils';

export const MemoPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedDate } = useDate();
  const navigate = useNavigate();

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // Level 1, 2만 접근 가능
  if (user && user.level > 2) {
    return <Navigate to="/" replace />;
  }

  const queryClient = useQueryClient();

  const { data: cleaningData, isLoading: cleaningLoading } = useQuery({
    queryKey: ['cleaning-list', dateStr],
    queryFn: () => cleaningApi.getList(dateStr),
  });

  // 전달사항 조회
  const { data: memosData } = useQuery({
    queryKey: ['memos', dateStr],
    queryFn: () => memoApi.getMemos(dateStr),
  });

  const cleaningList = cleaningData?.data.data?.list || [];
  const memos = memosData?.data.data?.memos || [];

  // 메모가 있는 객실만 필터링
  const roomsWithMemos = cleaningList.filter((item: CleaningItem) => (item.memoCount ?? 0) > 0);

  return (
    <div className="app-container">
      <Header
        showDatePicker
        showProfile
        userName={user?.name}
        onProfileClick={() => navigate('/settings')}
      />

      <main className="main-content">
        <h5 className="mb-3">
          <FiMessageSquare className="me-2" />
          담당자 메모
          {roomsWithMemos.length > 0 && (
            <Badge bg="primary" className="ms-2">
              {roomsWithMemos.length}
            </Badge>
          )}
        </h5>

        {cleaningLoading ? (
          <SkeletonList count={4} />
        ) : roomsWithMemos.length === 0 ? (
          <div className="text-center text-muted py-5">
            <FiMessageSquare size={48} className="mb-3 opacity-50" />
            <p>이 날짜에 등록된 메모가 없습니다.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {roomsWithMemos.map((item: CleaningItem) => {
              const roomMemo = memos.find((m: Memo) => m.roomId === item.roomId);
              return (
                <RoomMemoCard
                  key={item.id}
                  item={item}
                  dateStr={dateStr}
                  existingMemo={roomMemo?.memo || ''}
                  queryClient={queryClient}
                />
              );
            })}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

// 객실별 메모 카드 컴포넌트
interface RoomMemoCardProps {
  item: CleaningItem;
  dateStr: string;
  existingMemo: string;
  queryClient: ReturnType<typeof useQueryClient>;
}

const RoomMemoCard: React.FC<RoomMemoCardProps> = ({ item, dateStr, existingMemo, queryClient }) => {
  const [viewerImage, setViewerImage] = useState<string | null>(null);
  const [memoInput, setMemoInput] = useState(existingMemo);
  const [messageInput, setMessageInput] = useState('');

  const { data: commentsData, isLoading } = useQuery({
    queryKey: ['memo-comments', dateStr, item.roomId],
    queryFn: () => memoApi.getComments(dateStr, item.roomId),
  });

  const comments = commentsData?.data.data?.comments || [];

  // 전달사항 저장 mutation
  const saveMemoMutation = useMutation({
    mutationFn: () => memoApi.saveMemo(dateStr, item.roomId, memoInput),
    onSuccess: () => {
      toast.success('전달사항이 저장되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['memos'] });
      queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
    },
    onError: () => {
      toast.error('전달사항 저장에 실패했습니다.');
    },
  });

  // 메시지 전송 mutation
  const sendMessageMutation = useMutation({
    mutationFn: () => messageApi.sendMessage(item.userId!, messageInput, dateStr),
    onSuccess: () => {
      toast.success('메시지가 전송되었습니다.');
      setMessageInput('');
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
    onError: () => {
      toast.error('메시지 전송에 실패했습니다.');
    },
  });

  const handleSendMessage = () => {
    if (!messageInput.trim()) {
      toast.error('메시지를 입력해주세요.');
      return;
    }
    sendMessageMutation.mutate();
  };

  return (
    <>
    <ImageViewerModal
      show={!!viewerImage}
      imageUrl={viewerImage}
      onHide={() => setViewerImage(null)}
    />
    <Card className="shadow-sm">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <div>
          <span className="fw-bold">
            {formatRoomAddress(item.roomInfo?.room_address) || item.roomId}
          </span>
          {item.isJaesil && (
            <Badge bg="warning" text="dark" className="ms-2">재실</Badge>
          )}
          {item.userName && (
            <Badge bg="secondary" className="ms-2">
              <FiUser className="me-1" />
              {item.userName}
            </Badge>
          )}
        </div>
        <Badge bg="info" pill>
          <FiMessageSquare className="me-1" />
          {item.memoCount}
        </Badge>
      </Card.Header>
      <Card.Body>
        {/* 담당자 메모 목록 */}
        {isLoading ? (
          <div className="text-center text-muted py-2">로딩 중...</div>
        ) : comments.length === 0 ? (
          <div className="text-muted mb-3">메모가 없습니다.</div>
        ) : (
          <div className="d-flex flex-column gap-3 mb-3">
            {comments.map((comment: MemoComment) => (
              <div key={comment.id} className="border-bottom pb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">
                    <FiUser className="me-1" />
                    {comment.user.name}
                  </span>
                  <small className="text-muted">
                    <FiClock className="me-1" />
                    {formatDbTime(comment.createdAt)}
                  </small>
                </div>
                <p className="mb-1 small" style={{ whiteSpace: 'pre-wrap' }}>
                  {comment.comment}
                </p>
                {comment.images && comment.images.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {comment.images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img.thumbnailUrl || img.imageUrl}
                        alt="Memo"
                        className="rounded"
                        style={{ maxHeight: '80px', cursor: 'pointer' }}
                        onClick={() => setViewerImage(img.imageUrl)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 전달사항 & DM 아코디언 */}
        <Accordion>
          {/* 전달사항 */}
          <Accordion.Item eventKey="memo">
            <Accordion.Header className="py-0">
              <small>
                <FiEdit3 className="me-1" />
                {formatRoomAddress(item.roomInfo?.room_address) || item.roomId}객실 전달사항 {existingMemo && '(작성됨)'}
              </small>
            </Accordion.Header>
            <Accordion.Body className="p-2">
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="담당자에게 전달할 사항을 입력하세요..."
                value={memoInput}
                onChange={(e) => setMemoInput(e.target.value)}
                className="mb-2"
                style={{ fontSize: '14px' }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={() => saveMemoMutation.mutate()}
                disabled={saveMemoMutation.isPending}
              >
                {saveMemoMutation.isPending ? '저장 중...' : '저장'}
              </Button>
            </Accordion.Body>
          </Accordion.Item>

          {/* DM - 담당자가 있을 때만 */}
          {item.userId && (
            <Accordion.Item eventKey="dm">
              <Accordion.Header className="py-0">
                <small>
                  <FiSend className="me-1" />
                  담당자 다이렉트 메시지 ({item.userName})
                </small>
              </Accordion.Header>
              <Accordion.Body className="p-2">
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="메시지를 입력하세요..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  className="mb-2"
                  style={{ fontSize: '14px' }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSendMessage}
                  disabled={sendMessageMutation.isPending}
                >
                  <FiSend className="me-1" />
                  {sendMessageMutation.isPending ? '전송 중...' : '보내기'}
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>
      </Card.Body>
    </Card>
    </>
  );
};
